import { initialProps } from '@utils/initial'
import { Request } from '@group-devs/yfp7-global-modules'
import Error from '../404'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import InfiniteScroll from "react-infinite-scroll-component";

import { Row, Col, ProgressBar, Button, DropdownButton, Dropdown } from 'react-bootstrap'
import { FaCheck, FaSortAmountDown, FaChevronDown, FaBars } from 'react-icons/fa'
import Link from 'next/link'
import { WellnessLayout } from '@components/Block'
import { HeroWrapper } from '@group-devs/yfp7-global-modules/components/Block'
import params from '@data/params'
import sectionData from '@data/sections'
import categories from '@data/wellnessCategories'
import Image from 'next/image'

const request = new Request(process.env.apiDomain)

export async function getServerSideProps({ req, res, query }) {
    return {
        props: await initialProps(req, res, query)
    }
}

const getImageSource = (article) => {
    const thumbnail = article.theThumbnail || article.thumbnail || article.featured_image || article.image;
    return (!thumbnail || thumbnail === '' || thumbnail === 'null') ? params.noimage : thumbnail;
};

const SectionWrapper = (props) => {

    const router = useRouter()
    const route = router.query.section
    const section = sectionData.filter((e) => e.route == route)

    if (!section[0]) return <Error statusCode="404" />

    const cat1 = categories.slice(0, 7);
    const cat2 = categories.slice(7, 12);

    const userId = props.auth?.site_owner ? props.auth.userId : null
    const contactId = props.auth?.site_user ? props.auth.contactId : null
    const accountId = props.site ? props.site.accountId : null

    const { data: profile } = request.account.swrAccountProfile(accountId);
    const { data: shaklee } = request.account.swrShakleeInfo(accountId, profile ? profile.user.id : profile);

    const country = shaklee && shaklee.shaklee_country ? shaklee.shaklee_country : '232';

    const { data: info } = request.account.swrAccountProfileData(accountId, "account_user_trial_info");
    const config = info && info.data ? JSON.parse(info.data) : ''
    const tag = config ? config.company_name : 'Shaklee';

    // Searchable article titles for header search box
    const { data: recentArticles } = request.content.swrArticles({ section: "wellness-center", tags: [tag], except_types: [13], sort: "DATE_DESC", limit: 50, user_id: userId, contact_id: contactId, account_id: accountId, country })
    const { data: replaysArticles } = request.content.swrArticles({ section: "wellness-center", tags: [tag], type: 11, sort: "DATE_DESC", limit: 20, user_id: userId, contact_id: contactId, account_id: accountId, country })
    const { data: videosArticles } = request.content.swrArticles({ section: "wellness-center", tags: [tag], type: 2, sort: "DATE_DESC", limit: 20, user_id: userId, contact_id: contactId, account_id: accountId, country })

    const searchableItems = useMemo(() => {
        const all = [...(recentArticles || []), ...(replaysArticles || []), ...(videosArticles || [])]
        const unique = new Map()
        all.forEach(a => {
            if (!unique.has(a.id)) {
                unique.set(a.id, { id: a.id, slug: a.slug, title: a.title })
            }
        });
        return Array.from(unique.values())
    }, [recentArticles, replaysArticles, videosArticles]);

    // Infinite Scroll System
    const pageSize = 12;   // MUCH faster load than 10000
    const [search, setSearch] = useState("")
    const [filter, setFilter] = useState(null)
    const [sort, setSort] = useState("DATE_DESC")
    const [articles, setArticles] = useState([])
    const [offset, setOffset] = useState(0)
    const [hasMore, setHasMore] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [fetchError, setFetchError] = useState(null)
    const [showNoContent, setShowNoContent] = useState(false)

    const isInitializedRef = useRef(false)
    const isMountedRef = useRef(true)

    // Fetch Articles with Limit/Offset
    const fetchAllArticles = useCallback(async (category = null, sortVal = "DATE_DESC", currentOffset = 0, limit = pageSize) => {
        try {
            const sanitizedSearch = search ? encodeURIComponent(search) : "";
            const paramsX = {
                section: "wellness-center",
                tags: [tag],
                type: section[0].id,
                search: sanitizedSearch,
                sort: sortVal,
                limit: limit,
                offset: currentOffset,
                user_id: userId,
                contact_id: contactId,
                account_id: accountId,
                country: country,
                status: "published"
            };

            if (category) paramsX.category = category;

            const data = await request.content.articles(paramsX);
            return data || [];

        } catch (e) {
            console.error("Fetch error:", e);
            return [];
        }
    }, [search, tag, section, userId, contactId, accountId, country]);


    // Loads NEXT page of articles
    const loadArticles = useCallback(async (selectedFilter = filter) => {
        if (!tag || isLoading || !hasMore) return;

        setIsLoading(true);
        setFetchError(null);

        try {
            const newArticles = await fetchAllArticles(selectedFilter, sort, offset, pageSize);
            const articlesArray = Array.isArray(newArticles) ? newArticles : [];
            
            if (offset === 0 && newArticles.length === 0) {
                setShowNoContent(true);
                setArticles([]);
                setHasMore(false);
                return;
            }

            setArticles(prev => [...prev, ...articlesArray]);

            if (articlesArray.length < pageSize) {
                setHasMore(false);
            } else {
                setOffset(prev => prev + pageSize);
            }

        } catch (err) {
            console.error("Load error:", err);
            setFetchError("Error loading content");
        } finally {
            setIsLoading(false);
        }
    }, [tag, filter, sort, offset, fetchAllArticles, hasMore, isLoading]);

    // Reset pagination when filter / sort changes
    useEffect(() => {
        if (!isInitializedRef.current) return;

        setArticles([]);
        setOffset(0);
        setHasMore(true);
        loadArticles();
    }, [filter, sort]);

    // Initial page load
    useEffect(() => {
        if (!isInitializedRef.current && typeof window !== "undefined") {
            const savedFilter = localStorage.getItem("filter");
            setFilter(savedFilter || null);
            isInitializedRef.current = true;

            if (tag) loadArticles(savedFilter || null);
        }
    }, [tag]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Sticky filter buttons
    const [isSticky, setSticky] = useState(false);
    const ref = useRef(null);
    const handleScroll = () => {
        if (ref.current) {
            setSticky(ref.current.getBoundingClientRect().top <= 0);
        }
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // const toggleButton = useCallback((slug) => {
    //     const newFilter = slug || null;
    //     if (typeof window !== 'undefined') {
    //         localStorage.setItem('filter', newFilter || '');
    //     }

    //     setArticles([]);
    //     setOffset(0);
    //     setHasMore(true);
    //     setFilter(newFilter);

    //     // Load articles using the *new* filter directly
    //     // instead of waiting for useEffect to catch up
    //     setTimeout(() => {
    //         loadArticles(); // This will now use latest state
    //     }, 50); // small delay ensures React updates state first
    // }, [loadArticles]);

    // 1. Remove the useEffect that resets on [filter, sort]
    // This avoids double-firing or firing with stale offset/article states.

    // 2. Update toggleButton to handle the reset and immediate fetch
    const toggleButton = useCallback(async (slug) => {
        const newFilter = slug || null;
        if (typeof window !== 'undefined') {
            localStorage.setItem('filter', newFilter || '');
        }

        // Reset all states locally to ensure we don't use stale "hasMore" or "isLoading"
        setFilter(newFilter);
        setArticles([]);
        setOffset(0);
        setHasMore(true);
        setShowNoContent(false);
        
        // Manual Fetch: We pass the NEW filter and 0 offset directly 
        // to bypass the asynchronous state lag
        setIsLoading(true);
        try {
            const initialData = await fetchAllArticles(newFilter, sort, 0, pageSize);
            if (initialData.length === 0) {
                setShowNoContent(true);
                setHasMore(false);
            } else {
                setArticles(initialData);
                if (initialData.length < pageSize) {
                    setHasMore(false);
                } else {
                    setOffset(pageSize);
                }
            }
        } catch (err) {
            setFetchError("Error loading content");
        } finally {
            setIsLoading(false);
        }
    }, [fetchAllArticles, sort, pageSize]);

    console.log('articles:',articles);

    const classes = "text-reset border-0 rounded-0 bg-transparent px-4 py-3";

    return (
        <WellnessLayout global={props} searchItems={searchableItems}>
            <div className="videolibrary wellnessCenter mb-5">
                <HeroWrapper 
                    title={section[0].long_name}
                    description={section[0].text}
                    classname={`bg-${section[0].route}`}
                />

                {/* Sticky Categories */}
                <div className={`sticky-wrapper text-center bg-white position-sticky top-0 zindex-1 ${isSticky ? 'sticky' : ''}`} ref={ref}>
                    <div className="d-md-block d-none">
                        <Button active={!filter} onClick={() => toggleButton(null)} className={classes}>All</Button>
                        
                        {cat1.map((data, idx) => (
                            <Button
                                key={idx}
                                active={filter === data.slug}
                                onClick={() => toggleButton(data.slug)}
                                className={classes}
                            >
                                {data.name}
                            </Button>
                        ))}

                        <DropdownButton id="dropdown-more-button" title="More" className="d-inline-block bg-white dp-more" variant="" alignRight>
                            {cat2.map((data, idx) => (
                                data.slug !== "recipes" &&
                                <Dropdown.Item key={idx} as="button" onClick={() => toggleButton(data.slug)} className="text-reset">
                                    <img width="30" src={data.icon} className="mr-1 moreitem"/> {data.name}
                                </Dropdown.Item>
                            ))}
                        </DropdownButton>
                    </div>

                    {/* Mobile */}
                    <div className="d-md-none d-block">
                        <Dropdown className="py-md-3 py-2">
                            <Dropdown.Toggle variant="secondary"><FaBars/> Categories</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => toggleButton(null)}>All</Dropdown.Item>
                                {cat1.map((data, idx) => (
                                    <Dropdown.Item key={idx} onClick={() => toggleButton(data.slug)}>{data.name}</Dropdown.Item>
                                ))}
                                {cat2.map((data, idx) => (
                                    <Dropdown.Item key={idx} onClick={() => toggleButton(data.slug)}>{data.name}</Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <hr className="mt-0"/>
                </div>

                <div className="wellnessCenter cardscategory m-auto">
                    
                    <div className="d-flex align-items-center">
                        <DropdownButton id="dropdown-basic-button" title={<><FaSortAmountDown/> Sort: { params.sort[sort] } <FaChevronDown className="h7 ml-3" size={9}/></>} className="sortdropdown my-5">
                            <Dropdown.Item onClick={() => setSort("TITLE_ASC")}>{ params.sort.TITLE_ASC }</Dropdown.Item>
                            <Dropdown.Item onClick={() => setSort("DATE_DESC")}>{ params.sort.DATE_DESC }</Dropdown.Item>
                        </DropdownButton>

                        {/* {articles.length > 0 && (
                            <span className="ml-3 text-muted">
                                Showing {articles.length} article{articles.length !== 1 ? 's' : ''}
                            </span>
                        )} */}
                    </div>

                    {fetchError && (
                        <div className="alert alert-warning text-center">
                            {fetchError}
                            <button 
                                className="btn btn-link btn-sm ml-2" 
                                onClick={() => {
                                    setFetchError(null);
                                    loadArticles();
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {showNoContent && (
                        <h4 className="text-center py-5">No Content</h4>
                    )}

                    {/* Article Grid with Infinite Scroll */}
                    {!showNoContent && (
                        <InfiniteScroll
                            dataLength={articles.length}
                            next={loadArticles}
                            hasMore={hasMore}
                            loader={<h4 className="text-center py-5">Loading...</h4>}
                            endMessage={
                                <p className="text-center py-4 text-muted">
                                    <b>No more articles</b>
                                </p>
                            }
                        >
                            <div className="px-3">
                                <Row>
                                    {articles.map((data, idx) => {
                                        const watched = data.watched_data ? JSON.parse(data.watched_data) : "";
                                        const percentage = watched ? watched.percentage : 0;

                                        return (
                                            <Col md={3} sm={6} className="mb-4" key={`${data.id}-${idx}`}>
                                                <Link href={"/a/" + data.slug}>
                                                    <a className="h7 text-reset text-decoration-none">
                                                        <Image
                                                            src={getImageSource(data)}                                                            
                                                            alt={data.title}                                                             
                                                            width={310}                                                             
                                                            height={160}                                                             
                                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 310px" 
                                                            placeholder="blur"
                                                            blurDataURL={params.noimage}
                                                            loading={idx < 8 ? "eager" : "lazy"}                                                             
                                                            priority={idx < 8}                                                             
                                                            quality={50}     
                                                            unoptimized={true}                                                        
                                                            style={{ objectFit: "cover" }}                                                         
                                                        />

                                                        <div className="mt-1 mb-3 pbar">
                                                            {data.is_read == 1 && (
                                                                <p className="mb-0 read h8 text-right position-relative">
                                                                    <FaCheck className="cardCheck" /> You read this
                                                                </p>
                                                            )}
                                                            {data.is_downloaded == 1 && (
                                                                <p className="read h8 text-right position-relative">
                                                                    <FaCheck className="cardCheck" /> Downloaded
                                                                </p>
                                                            )}
                                                            {data.is_watched == 1 && (
                                                                <ProgressBar now={percentage} />
                                                            )}
                                                        </div>

                                                        <p>{data.title}</p>
                                                    </a>
                                                </Link>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        </InfiniteScroll>
                    )}

                </div>
            </div>

            {/* All your original CSS preserved */}
            <style jsx global>{`
                button.active{
                    border-bottom:2px solid #43a649!important;
                }
                #dropdown-more-button{
                    padding: 0 1.5rem!important;
                    position:relative;
                }
                .dp-more .dropdown-menu{
                    grid-template-columns: repeat(2, 1fr);
                    top: 21px!important;
                } 
                .sticky-wrapper button{
                    border-bottom:3px solid transparent!important;
                }
                .sticky-wrapper button.active{
                    border-color:#43a649!important;
                } 
                .sticky {
                    box-shadow: 0 5px 5px -5px #333;
                }
                .sticky button.active{
                    background-color: #43a649!important;
                    color: white!important;
                    padding: .25rem 1rem!important;
                    border-radius: .25rem!important;
                }
                .dp-more .dropdown-item:nth-of-type(4) .moreitem{
                    width:25px!important;
                }
                .dp-more .dropdown-item.active, .dp-more .dropdown-item:active{
                    background-color:#f8f9fa!important;
                }
                .cardscategory{
                    max-width:1334px;
                }
                @media (max-width: 991px) {
                    .infinite-scroll-component__outerdiv{
                        text-align: center;
                    }
                    .wellnessCenter .pbar .read{
                        text-align:center!important;
                    }
                }
                @media (max-width: 767px) {
                    .sticky-wrapper.position-sticky{
                        top: 48px !important;
                    }
                }
            `}</style>
        </WellnessLayout>
    );
}

export default SectionWrapper;
